/* =========================================================
   Image-gallery carousel (storefront).
   Galleries are server-rendered from article.content as a
   .mp-gallery-wrap (an overflow-x:auto scroller of fixed-fraction
   .mp-gallery-item figures). The markup works with zero JS — this
   only *enhances* it:
     - click-drag anywhere to pan (mouse only; touch keeps native swipe)
     - vertical wheel → horizontal pan when the gallery is the dominant axis
     - injected prev/next arrows that page one item at a time
     - a grab cursor + live arrow enable/disable only while it overflows
   Self-contained and idempotent: safe to load on any page; re-scans when a
   feed "See more" reveals a previously-clipped card.
   ========================================================= */
(function () {
  "use strict";

  // How far the arrows page: one item + the track gap (falls back to ~80% of
  // the viewport so a gallery with odd markup still advances sensibly).
  function pageStep(wrap) {
    var track = wrap.querySelector(".mp-gallery-track");
    var item = wrap.querySelector(".mp-gallery-item");
    if (item) {
      var gap = 0;
      if (track) {
        var cs = window.getComputedStyle(track);
        gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      }
      return item.offsetWidth + gap;
    }
    return Math.max(1, Math.round(wrap.clientWidth * 0.8));
  }

  function wire(wrap) {
    if (wrap.__galWired) return;
    wrap.__galWired = true;

    // Wrap the scroller in a positioned frame so the arrows can sit over its
    // edges without scrolling away with the content.
    var frame = document.createElement("div");
    frame.className = "mp-gallery-frame";
    if (wrap.parentNode) {
      wrap.parentNode.insertBefore(frame, wrap);
      frame.appendChild(wrap);
    }

    var prev = document.createElement("button");
    prev.type = "button";
    prev.className = "mp-gallery-arrow mp-gallery-arrow--prev";
    prev.setAttribute("aria-label", "Previous image");
    prev.innerHTML = "‹"; // ‹
    var next = document.createElement("button");
    next.type = "button";
    next.className = "mp-gallery-arrow mp-gallery-arrow--next";
    next.setAttribute("aria-label", "Next image");
    next.innerHTML = "›"; // ›
    frame.appendChild(prev);
    frame.appendChild(next);

    // Decode images off the main thread so a new photo scrolling into view during
    // a drag doesn't stutter the pan (applies to already-saved galleries too).
    var imgs = wrap.querySelectorAll(".mp-gallery-img");
    for (var n = 0; n < imgs.length; n++) {
      imgs[n].decoding = "async";
      // Stop the browser's native image drag-and-drop — it fires mid-pan, cancels
      // our pointer drag (pointercancel), and leaves the next drag "locked up".
      imgs[n].draggable = false;
      if (!imgs[n].getAttribute("loading")) imgs[n].setAttribute("loading", "lazy");
    }
    // Belt-and-suspenders: kill any native drag that starts inside the scroller.
    wrap.addEventListener("dragstart", function (e) { e.preventDefault(); });

    // Cache the scrollable extent so the scroll + drag hot paths NEVER read
    // layout. Reading scrollWidth/clientWidth forces a synchronous reflow; doing
    // it on every scroll tick (as the first version did) is what made dragging
    // janky — the first drag ran while layout was warm, later ones fought the
    // reflows. Re-measure only when the size can actually change.
    var maxScroll = 0;
    function setDisabled(btn, v) {
      if (btn.disabled !== v) btn.disabled = v; // skip redundant DOM writes
    }
    function updateArrows() {
      var can = maxScroll > 1;
      setDisabled(prev, !can || wrap.scrollLeft <= 1);
      setDisabled(next, !can || wrap.scrollLeft >= maxScroll - 1);
    }
    function measure() {
      maxScroll = wrap.scrollWidth - wrap.clientWidth;
      wrap.classList.toggle("is-scrollable", maxScroll > 1);
      updateArrows();
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("load", measure); // widths settle as images/fonts load

    // Arrow enable/disable on scroll — throttled to one rAF, reading only the
    // cheap scrollLeft (never re-measuring).
    var arrowsTick = false;
    wrap.addEventListener(
      "scroll",
      function () {
        if (arrowsTick) return;
        arrowsTick = true;
        requestAnimationFrame(function () {
          arrowsTick = false;
          updateArrows();
        });
      },
      { passive: true }
    );

    prev.addEventListener("click", function () {
      wrap.scrollBy({ left: -pageStep(wrap), behavior: "smooth" });
    });
    next.addEventListener("click", function () {
      wrap.scrollBy({ left: pageStep(wrap), behavior: "smooth" });
    });

    // NOTE: no wheel→horizontal-pan handler — scrolling the mouse wheel /
    // trackpad over the gallery scrolls the PAGE normally. Panning is by
    // click-drag, native touch swipe, the arrows, or the native scrollbar /
    // trackpad horizontal gesture (all handled by the browser's own overflow).

    // Click-drag to pan. Mouse only — touch devices already swipe-scroll natively.
    // Direct scrollLeft per pointermove, exactly like the (smooth) comparison
    // table — pointermove already fires at the input rate, so coalescing it into
    // rAF only added latency/unevenness. maxScroll is cached, so nothing here
    // reads layout.
    var down = false, sx = 0, sl = 0, moved = false;
    wrap.addEventListener("pointerdown", function (e) {
      if (e.pointerType && e.pointerType !== "mouse") return;
      if (maxScroll <= 0) return;
      down = true;
      moved = false;
      sx = e.clientX;
      sl = wrap.scrollLeft;
      wrap.classList.add("is-grabbing");
      try {
        wrap.setPointerCapture(e.pointerId);
      } catch (err) {
        /* best-effort */
      }
    });
    wrap.addEventListener("pointermove", function (e) {
      if (!down) return;
      var dx = e.clientX - sx;
      if (Math.abs(dx) > 4) moved = true;
      wrap.scrollLeft = sl - dx;
    });
    function end() {
      down = false;
      wrap.classList.remove("is-grabbing");
    }
    wrap.addEventListener("pointerup", end);
    wrap.addEventListener("pointercancel", end);
    // Swallow the click that terminates a drag so it never activates an image link.
    wrap.addEventListener(
      "click",
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );
  }

  function init() {
    var wraps = document.querySelectorAll(".mp-gallery-wrap");
    for (var i = 0; i < wraps.length; i++) wire(wraps[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  // Feed "See more" expands a card that may contain a gallery — re-scan after it opens.
  document.addEventListener("click", function (e) {
    if (e.target && e.target.closest && e.target.closest(".li-more")) {
      setTimeout(init, 50);
    }
  });
})();
